import { supabase } from "@foundation/utils/supabaseClient";

// Enhanced client interface that now supports CRM companies
export interface EnhancedClient {
  id: string;
  name: string;
  contactPerson: string; // Empty for companies, kept for compatibility
  email: string;
  phone: string;
  company: string; // Same as name for companies
  industry: string;
  source: "crm_company"; // Changed to company source
  organisationId: string;
  organisationName?: string;
  createdAt: Date;
  updatedAt: Date;
}

// API response interface
interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta?: {
    total: number;
    crmCompanies: number;
  };
}

export class EnhancedClientService {
  private baseUrl: string;
  private apiKey: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";
    this.apiKey = import.meta.env.VITE_API_KEY || "scopingai";
  }

  /**
   * Get current user ID from Supabase session
   */
  private async getCurrentUserId(): Promise<string> {
    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError) {
      console.error("Error getting user:", userError);
      throw userError;
    }

    const userId = userData.user?.id;
    if (!userId) {
      throw new Error("User not authenticated");
    }

    return userId;
  }

  /**
   * Get user's organizations
   */
  async getUserOrganizations(): Promise<string[]> {
    try {
      const userId = await this.getCurrentUserId();

      // Get all organizations the user is a member of
      const { data, error } = await supabase
        .from("base_organisation_members")
        .select("organisation_id")
        .eq("user_id", userId);

      if (error) {
        console.error("Error getting user organizations:", error);
        return [];
      }

      return data.map((item) => item.organisation_id);
    } catch (error) {
      console.error("Error getting user organizations:", error);
      return [];
    }
  }

  /**
   * Get CRM companies from ALL user's organizations
   */
  async getOrganizationContacts(): Promise<EnhancedClient[]> {
    try {
      const userId = await this.getCurrentUserId();
      const organisationIds = await this.getUserOrganizations();

      if (organisationIds.length === 0) {
        console.warn(
          "User is not a member of any organization, returning empty array"
        );
        return [];
      }

      console.log(
        `🔍 Fetching CRM companies from ${organisationIds.length} organizations`
      );

      // Fetch CRM companies directly from Supabase
      const { data, error } = await supabase
        .from("crm_companies")
        .select(
          `
          *,
          organisations (
            id,
            name
          )
        `
        )
        .in("organisation_id", organisationIds)
        .order("name");

      if (error) {
        console.error("Error fetching CRM companies:", error);
        throw error;
      }

      const companies = (data || []).map(this.transformCrmCompany);

      console.log(
        `✅ Loaded ${companies.length} CRM companies from ${organisationIds.length} organizations`
      );

      return companies;
    } catch (error) {
      console.error("Error fetching organization companies:", error);
      return [];
    }
  }

  /**
   * Check if user has companies in their organizations
   */
  async hasCompanies(): Promise<boolean> {
    try {
      const organisationIds = await this.getUserOrganizations();
      if (organisationIds.length === 0) {
        return false;
      }

      const { count, error } = await supabase
        .from("crm_companies")
        .select("*", { count: "exact", head: true })
        .in("organisation_id", organisationIds);

      if (error) {
        console.error("Error checking companies:", error);
        return false;
      }

      return (count || 0) > 0;
    } catch (error) {
      console.error("Error checking companies:", error);
      return false;
    }
  }

  /**
   * Create a new CRM company
   */
  async createCrmContact(companyData: {
    name: string;
    email?: string;
    phone?: string;
  }): Promise<EnhancedClient> {
    try {
      const userId = await this.getCurrentUserId();

      // Get user's organization IDs
      const organisationIds = await this.getUserOrganizations();

      if (organisationIds.length === 0) {
        throw new Error("User is not a member of any organization");
      }

      // Use the first organization ID (user's primary organization)
      const organisationId = organisationIds[0];

      // Create the company in CRM
      const companyInsertData: any = {
        organisation_id: organisationId,
        name: companyData.name,
        owner_id: userId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Add optional fields only if they have values
      if (companyData.email?.trim()) {
        companyInsertData.email = companyData.email.trim();
      }
      if (companyData.phone?.trim()) {
        companyInsertData.phone = companyData.phone.trim();
      }

      console.log("📝 Prepared company insert data:", companyInsertData);

      const { data, error } = await supabase
        .from("crm_companies")
        .insert(companyInsertData)
        .select(
          `
          *,
          organisations (
            id,
            name
          )
        `
        )
        .single();

      if (error) {
        console.error("Error creating CRM company:", error);
        throw error;
      }

      const newCompany = this.transformCrmCompany(data);
      console.log(`✅ Successfully created CRM company: ${newCompany.name}`);

      return newCompany;
    } catch (error) {
      console.error("Error creating CRM company:", error);
      throw error;
    }
  }

  /**
   * Transform CRM company data to EnhancedClient interface
   */
  private transformCrmCompany(crmCompany: any): EnhancedClient {
    return {
      id: crmCompany.id,
      name: crmCompany.name,
      contactPerson: "", // Empty for companies
      email: crmCompany.email || "",
      phone: crmCompany.phone || "",
      company: crmCompany.name, // Same as name for companies
      industry: crmCompany.custom_fields?.industry || "",
      source: "crm_company" as const,
      organisationId: crmCompany.organisation_id,
      organisationName: crmCompany.organisations?.name,
      createdAt: new Date(crmCompany.created_at),
      updatedAt: new Date(crmCompany.updated_at),
    };
  }

  /**
   * Search companies by query
   */
  searchContacts(companies: EnhancedClient[], query: string): EnhancedClient[] {
    if (!query.trim()) {
      return companies;
    }

    const searchTerm = query.toLowerCase();
    return companies.filter(
      (company) =>
        company.name.toLowerCase().includes(searchTerm) ||
        company.email.toLowerCase().includes(searchTerm) ||
        company.phone.toLowerCase().includes(searchTerm)
    );
  }

  /**
   * Get company statistics
   */
  getContactStats(companies: EnhancedClient[]) {
    return {
      total: companies.length,
      crmCompanies: companies.length, // All are CRM companies now
    };
  }

  /**
   * Get organization statistics
   */
  async getOrganizationStats(): Promise<any> {
    try {
      const userId = await this.getCurrentUserId();
      const organisationIds = await this.getUserOrganizations();

      if (organisationIds.length === 0) {
        return {
          totalContacts: 0,
          totalCompanies: 0,
          organizations: 0,
        };
      }

      // Get contact count
      const { count: contactCount, error: contactError } = await supabase
        .from("crm_companies")
        .select("*", { count: "exact", head: true })
        .in("organisation_id", organisationIds);

      if (contactError) {
        console.error("Error getting contact count:", contactError);
      }

      // Get company count
      const { count: companyCount, error: companyError } = await supabase
        .from("crm_companies")
        .select("*", { count: "exact", head: true })
        .in("organisation_id", organisationIds);

      if (companyError) {
        console.error("Error getting company count:", companyError);
      }

      return {
        totalContacts: contactCount || 0,
        totalCompanies: companyCount || 0,
        organizations: organisationIds.length,
      };
    } catch (error) {
      console.error("Error getting organization stats:", error);
      return {
        totalContacts: 0,
        totalCompanies: 0,
        organizations: 0,
      };
    }
  }

  /**
   * Check onboarding status for the current user
   * Only checks organization membership - if user is not in any organization, show onboarding
   */
  async checkOnboardingStatus(): Promise<{
    hasOrganization: boolean;
    organizationId?: string;
    hasProfile: boolean;
    isOnboarded: boolean;
  }> {
    try {
      const userId = await this.getCurrentUserId();

      // Check if user is a member of any organization
      const { data: membership, error: membershipError } = await supabase
        .from("base_organisation_members")
        .select("organisation_id")
        .eq("user_id", userId)
        .limit(1)
        .single();

      if (membershipError) {
        // User is not a member of any organization - needs onboarding
        console.log("❌ User is not a member of any organization - showing onboarding");
        return {
          hasOrganization: false,
          organizationId: undefined,
          hasProfile: true, // Assume profile exists since user is authenticated
          isOnboarded: false, // Not onboarded because no organization
        };
      }

      const hasOrganization = !!membership;
      const organizationId = membership?.organisation_id;

      console.log("✅ User is member of organization:", {
        hasOrganization,
        organizationId,
      });

      // User has organization membership - no onboarding needed
      return {
        hasOrganization,
        organizationId,
        hasProfile: true,
        isOnboarded: true, // Onboarded because user has organization
      };
    } catch (error) {
      console.error("Error checking onboarding status:", error);
      // On error, assume user needs onboarding for safety
      return {
        hasOrganization: false,
        organizationId: undefined,
        hasProfile: false,
        isOnboarded: false,
      };
    }
  }

  /**
   * Refresh companies data
   */
  async refreshContacts(): Promise<EnhancedClient[]> {
    return this.getOrganizationContacts();
  }
}

// Export singleton instance
export const enhancedClientService = new EnhancedClientService();
